import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Stack,
  Divider,
  Container,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { setSnackError } from "../../Reducers/snackErrors";
import API from "../../api/api";
import { setSuccessSnack } from "../../Reducers/snackSuccess";
import usePermissions from "../../Reducers/usePermissions";

// Updated Validation Schema with new field
const validationSchema = Yup.object({
  id: Yup.number().nullable(),
  program_name: Yup.string()
    .required("Program name is required")
    .max(150, "Program name must be 150 characters or less"),
  admin_id: Yup.number(),
  organization_id: Yup.number(),
  timeframe_required: Yup.boolean(),
  survey_required: Yup.boolean(),
});

const AddProgram = ({ open, onClose, initialData }) => {
  const role = useSelector((state) => state.role);
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  const initialValues = {
    id: initialData?.id || null,
    admin_id: role.id,
    program_name: initialData?.program_name || "",
    organization_id: role.organization_id,
    timeframe_required: initialData?.timeframe_required || false, // Added new field
    survey_required: initialData?.survey_required || false
  };

  const handleSubmit = async (values) => {
    try {
      let response = null;
      if (values.id != null) {
        response = await API.post('/update_program', values);
      } else {
        response = await API.post('/create_program', values);
      }
      
      if (response.data.status === 'Updated') {
        dispatch(setSuccessSnack({title: 'Success', body: `Updated successfully`}));
        return;
      }
      if (response.data.status === 'OK') {
        dispatch(setSuccessSnack({
          title: 'Success', 
          body: `Created program successfully with id ${response.data.id}`
        }));
        return;
      }
    } catch(error) {
      console.log(error);
      if (error.isAuthError || error.isForbidden) {
        return;
      } 
      if (error.isNetworkError) {
        dispatch(setSnackError({ 
          title: 'Connection Error', 
          body: error.message 
        }));
      } else {
        dispatch(setSnackError({
          title: 'Error found',
          body: error.message || 'Unable to add Program.'
        }));
      }
      return;
    } finally {
      onClose();
      
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="h6" fontWeight={'bold'}>
          {initialData?.id ? "Edit Program" : "Add Program"}
        </Typography>
        <Typography variant="body2">
          Add/update a program below.
        </Typography>
      </DialogTitle>
      <Divider />

      <Container>
        <Typography variant="body2" fontWeight={'light'} color="secondary.text">
          Example : "Math intervention" each student must have a deticated timeframe 8:00am-8:30am, with a end of session survey.
        </Typography>
      </Container>
      
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, setFieldValue }) => (
          <Form>
            <DialogContent>
              <TextField
                fullWidth
                margin="dense"
                label="Program Name"
                name="program_name"
                value={values.program_name}
                onChange={handleChange}
                error={touched.program_name && Boolean(errors.program_name)}
                helperText={touched.program_name && errors.program_name}
              />
              <Stack spacing={1}>
              {/* Added Checkbox for timeframe_required */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.timeframe_required}
                    onChange={(e) => setFieldValue('timeframe_required', e.target.checked)}
                    name="timeframe_required"
                    color="secondary"
                  />
                }
                label="Require timeframe?"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.survey_required}
                    onChange={(e) => setFieldValue('survey_required', e.target.checked)}
                    name="survey_required"
                    color="secondary"
                  />
                }
                label="Require Survey?"
              />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>Cancel</Button>
              <Button  
                disabled={!hasPermission('write', 'program')}
                type="submit" 
                variant="contained"
              >
                Submit
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddProgram;